import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Globe2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionId = (() => {
  let s = localStorage.getItem("ao2_chat_session");
  if (!s) {
    s = `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("ao2_chat_session", s);
  }
  return s;
})();

const WELCOME = {
  role: "assistant",
  content:
    "Welcome to Arco Orbis2. I'm the Risk Advisor. If you have a security, travel, crisis or facility concern, tell me a little about it and I'll share some professional, high-level guidance.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm unable to respond right now. Please try again shortly, or use the contact form to reach Arco Orbis2." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200, damping: 18 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[70] h-14 w-14 rounded-full bg-gold text-black flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:bg-gold-light transition-colors duration-300"
        aria-label="Open Risk Advisor chat"
        data-testid="chat-launcher"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[70] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[75vh] flex flex-col rounded-2xl overflow-hidden glass border border-gold/25 shadow-2xl"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-ink-surface/80">
              <span className="h-9 w-9 rounded-full border border-gold/40 flex items-center justify-center text-gold">
                <Globe2 size={18} />
              </span>
              <div>
                <div className="font-display font-light text-sm text-white leading-tight"><span className="notranslate" translate="no">Arco Orbis2</span> <span className="gold-text">Risk Advisor</span></div>
                <div className="text-[0.65rem] text-silver/50 font-body flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online · advisory guidance
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm font-body font-light leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-black rounded-br-sm"
                        : "bg-ink border border-white/10 text-silver/90 rounded-bl-sm"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-ink border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-gold" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-ink-surface/80">
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Describe your concern…"
                  className="flex-1 resize-none bg-ink border border-white/10 rounded-xl px-4 py-3 text-sm font-body text-white placeholder:text-silver/30 focus:border-gold/50 focus:outline-none transition-colors max-h-28"
                  data-testid="chat-input"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="h-11 w-11 shrink-0 rounded-xl bg-gold text-black flex items-center justify-center hover:bg-gold-light transition-colors disabled:opacity-40"
                  data-testid="chat-send"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
              <p className="mt-2 text-[0.6rem] text-silver/30 font-body text-center">General guidance only. For urgent situations contact local emergency services.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
